/* STEFANO - Slider delle foto nelle card prodotto (CSS in custom.css). Un solo listener per pagina, cosi' vale anche
   per le card caricate dopo (pagina 2, scroll infinito, filtri).
   - primo contatto con una card (mouse o tocco): anticipa le foto lazy e rende lo slider circolare, aggiungendo
     una copia dell'ultima foto in testa e della prima in coda; arrivati su una copia si torna alla foto vera
     senza che si veda (stessa immagine), quindi il giro dall'ultima alla prima e viceversa e' uno scorrimento normale;
   - passaggio del mouse (desktop): mostra subito la seconda foto, uscendo torna alla prima;
   - clic sulle frecce (desktop): scorre di una foto con transizione;
   - scroll: aggiorna il pallino attivo su mobile (lo scroll non risale, quindi si ascolta in cattura). */
(() => {
  let frame = 0;
  let touching = false;
  const timers = new WeakMap();

  const loopSize = (media) => (media.dataset.mimmuLoop ? 2 : 0);

  const makeLoop = (media) => {
    const images = media.querySelectorAll(':scope > img');

    if (media.dataset.mimmuLoop || images.length < 2 || getComputedStyle(media).direction === 'rtl') {
      return;
    }

    const first = images[0].cloneNode(true);
    const last = images[images.length - 1].cloneNode(true);

    [first, last].forEach((clone) => {
      clone.loading = 'eager';
      clone.setAttribute('aria-hidden', 'true');
    });

    media.dataset.mimmuLoop = 'true';
    media.prepend(last);
    media.append(first);
    media.scrollTo({ left: media.clientWidth, behavior: 'instant' });
  };

  // se ci si e' fermati su una copia, si passa alla foto vera (stessa immagine)
  const settle = (media) => {
    const width = media.clientWidth;

    if (!media.dataset.mimmuLoop || touching || !width) {
      return;
    }

    const total = media.children.length - 2;
    const position = Math.round(media.scrollLeft / width);

    if (position === 0) {
      media.scrollTo({ left: total * width, behavior: 'instant' });
    } else if (position === total + 1) {
      media.scrollTo({ left: width, behavior: 'instant' });
    }
  };

  const settleLater = (media) => {
    clearTimeout(timers.get(media));
    timers.set(media, setTimeout(() => {
      // lo scorrimento e' finito: la destinazione dei clic sulle frecce si azzera
      delete media.dataset.mimmuTarget;
      settle(media);
    }, 120));
  };

  document.addEventListener(
    'scroll',
    (event) => {
      const media = event.target;

      if (!media.classList || !media.classList.contains('mimmu-card-media')) {
        return;
      }

      settleLater(media);

      if (frame) {
        return;
      }

      frame = requestAnimationFrame(() => {
        frame = 0;

        const total = media.children.length - loopSize(media);
        const index = Math.round(Math.abs(media.scrollLeft) / media.clientWidth) - loopSize(media) / 2;

        media.parentElement.style.setProperty('--mimmu-i', ((index % total) + total) % total);
      });
    },
    { capture: true, passive: true }
  );

  document.addEventListener('click', (event) => {
    const arrow = event.target.closest('.mimmu-card-arrow');

    if (!arrow) {
      return;
    }

    const media = arrow.parentElement.querySelector('.mimmu-card-media');
    const step = arrow.classList.contains('mimmu-card-arrow--next') ? 1 : -1;

    makeLoop(media);

    // la destinazione parte dall'ultima foto richiesta e non dalla posizione a meta' animazione: con clic ravvicinati
    // ogni clic vale esattamente una foto
    const width = media.clientWidth;
    const last = media.children.length - 1;
    const base = media.dataset.mimmuTarget === undefined ? Math.round(media.scrollLeft / width) : Number(media.dataset.mimmuTarget);
    const target = Math.min(Math.max(base + step, 0), last);

    media.dataset.mimmuTarget = target;
    media.scrollTo({
      left: target * width,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
    settleLater(media);
  });

  const prepare = (event) => {
    const figure = event.target.closest && event.target.closest('.product-card__figure');

    if (!figure || figure.dataset.mimmuPreloaded) {
      return;
    }

    figure.dataset.mimmuPreloaded = 'true';
    figure.querySelectorAll('.mimmu-card-media img[loading="lazy"]').forEach((image) => {
      image.loading = 'eager';
      image.fetchPriority = 'auto';
    });

    const media = figure.querySelector('.mimmu-card-media');

    if (media) {
      makeLoop(media);
    }
  };

  const touchChange = (event) => {
    touching = event.touches.length > 0;

    if (!touching) {
      const media = event.target.closest && event.target.closest('.product-card__figure .mimmu-card-media');

      if (media) {
        settleLater(media);
      }
    }
  };

  // solo mouse: al passaggio sulla card si vede subito la seconda foto, uscendo si torna alla prima
  const hoverPhoto = (event, showSecond) => {
    const figure = event.pointerType === 'mouse' && event.target.closest && event.target.closest('.product-card__figure');
    const media = figure && figure.querySelector('.mimmu-card-media');

    if (!media || (event.relatedTarget && figure.contains(event.relatedTarget)) || media.children.length < 2) {
      return;
    }

    const offset = loopSize(media) / 2;

    delete media.dataset.mimmuTarget;
    media.scrollTo({ left: media.clientWidth * (offset + (showSecond ? 1 : 0)), behavior: 'instant' });
  };

  document.addEventListener('pointerover', (event) => {
    prepare(event);
    hoverPhoto(event, true);
  }, { passive: true });
  document.addEventListener('pointerout', (event) => hoverPhoto(event, false), { passive: true });
  document.addEventListener('touchstart', (event) => {
    touching = true;
    prepare(event);
  }, { capture: true, passive: true });
  document.addEventListener('touchend', touchChange, { capture: true, passive: true });
  document.addEventListener('touchcancel', touchChange, { capture: true, passive: true });
})();

/* STEFANO - Home, effetto "scorri sopra la sezione precedente" (CSS in custom.css): scrive l'altezza delle sezioni
   ferme in --mimmu-pinned-height. Gli stessi selettori sono nel blocco omonimo di custom.css. */
(() => {
  const observer = new ResizeObserver((entries) => {
    entries.forEach(({ target }) => target.style.setProperty('--mimmu-pinned-height', `${target.offsetHeight}px`));
  });

  const watch = () => {
    document
      .querySelectorAll('.shopify-section:has(+ [id$="__collection-list"]), .shopify-section:has(+ [id$="__video_zDVJgy"])')
      .forEach((section) => observer.observe(section));
  };

  watch();
  document.addEventListener('shopify:section:load', watch);
})();

/* STEFANO - Collezioni: scroll infinito. Quando la paginazione (nascosta da custom.css) si avvicina allo schermo,
   scarica la pagina successiva e ne aggiunge le card alla lista. Se il caricamento fallisce la paginazione torna
   visibile; senza JavaScript resta la paginazione normale. Dopo un filtro o un ordinamento il tema sostituisce
   il contenuto della sezione: un MutationObserver riaggancia tutto. */
(() => {
  const root = document.querySelector('.shopify-section--main-collection');

  if (!root) {
    return;
  }

  let loading = false;

  const emit = (name) => document.documentElement.dispatchEvent(new CustomEvent(name, { bubbles: true }));

  const observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadNext();
      }
    },
    // il margine superiore fa contare anche la paginazione appena superata (dopo un salto in fondo alla pagina)
    { rootMargin: '3000px 0px 600px 0px' }
  );

  const setup = () => {
    const main = root.querySelector('.collection__main');
    const nav = main && main.querySelector('.pagination');

    observer.disconnect();

    if (nav) {
      main.classList.add('mimmu-infinite');
      observer.observe(nav);
    }
  };

  const loadNext = async () => {
    const main = root.querySelector('.collection__main');
    const list = main && main.querySelector('product-list');
    const nav = main && main.querySelector('.pagination');
    const next = nav && nav.querySelector('a[rel="next"]');

    if (loading || !list || !next) {
      return;
    }

    loading = true;
    emit('theme:loading:start');

    try {
      const response = await fetch(next.href);

      if (!response.ok) {
        throw new Error(response.status);
      }

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const newList = doc.querySelector('.shopify-section--main-collection product-list');
      const newNav = doc.querySelector('.shopify-section--main-collection .pagination');

      if (!newList) {
        throw new Error('lista non trovata');
      }

      newList.querySelectorAll('product-card').forEach((card) => {
        card.removeAttribute('reveal-on-scroll');
        list.append(document.importNode(card, true));
      });

      if (newNav) {
        nav.replaceChildren(...Array.from(newNav.childNodes, (node) => document.importNode(node, true)));
      }
    } catch (error) {
      observer.disconnect();
      main.classList.remove('mimmu-infinite');
    } finally {
      loading = false;
      emit('theme:loading:end');

      // se la paginazione e' ancora vicina allo schermo, l'osservatore deve ripartire
      if (nav.isConnected && main.classList.contains('mimmu-infinite')) {
        observer.unobserve(nav);
        observer.observe(nav);
      }
    }
  };

  setup();
  new MutationObserver(setup).observe(root, { childList: true });
})();

/* STEFANO - Pagina prodotto: i pallini della galleria sono uno per foto, con 14-34 foto vanno a capo su piu' righe.
   Con piu' di 5 pallini visibili si mostra una finestra di 5 che scorre, con il pallino corrente al centro e i due
   ai lati piu' piccoli. Le regole di stile (classe mimmu-page-dots--windowed e variabile --mimmu-dot-opacity) sono
   nella Parte 1 di custom.css; fino a quando la barra e' pronta (data-mimmu-ready) una regola della Parte 2 la
   taglia gia' ai primi 5. Si aggiorna quando il tema riscrive aria-current; non ascolta lo scroll.
   Sulle altre pagine non fa nulla. */
(() => {
  const root = document.querySelector('.shopify-section--main-product');

  if (!root) {
    return;
  }

  const SIZE = 5;
  const CLASS = 'mimmu-page-dots--windowed';
  const watched = new WeakSet();

  // dimensione: piu' piccoli i pallini vicino al bordo della finestra, se da quel lato la serie continua
  const scaleAt = (distance) => (distance <= 0 ? 0.4 : distance === 1 ? 0.75 : 1);
  // colore: piu' chiari quanto piu' sono lontani dal pallino corrente
  const opacityAt = (distance) => (distance === 0 ? 1 : distance === 1 ? 0.58 : 0.4);

  const reset = (dot) => {
    dot.style.removeProperty('transform');
    dot.style.removeProperty('--mimmu-dot-opacity');
  };

  const render = (nav) => {
    const all = Array.from(nav.children);
    const dots = all.filter((dot) => !dot.hidden);

    all.forEach(reset);

    if (dots.length <= SIZE) {
      nav.classList.remove(CLASS);
      nav.style.removeProperty('width');
      nav.dataset.mimmuReady = 'true';
      return;
    }

    // misure dagli stili calcolati; con la barra nascosta (altro breakpoint) sono 0 e si riparte al ridimensionamento
    const dotSize = parseFloat(getComputedStyle(dots[0]).width);
    const gap = parseFloat(getComputedStyle(nav).columnGap) || 0;

    if (!dotSize) {
      return;
    }

    const step = dotSize + gap;
    const active = Math.max(0, dots.findIndex((dot) => dot.getAttribute('aria-current') === 'true'));
    const start = Math.max(0, Math.min(active - Math.floor(SIZE / 2), dots.length - SIZE));
    const end = start + SIZE - 1;

    nav.dataset.mimmuReady = 'true';
    nav.classList.add(CLASS);
    nav.style.width = `${dotSize * SIZE + gap * (SIZE - 1)}px`;

    dots.forEach((dot, index) => {
      const fromStart = start > 0 ? index - start : Infinity;
      const fromEnd = end < dots.length - 1 ? end - index : Infinity;

      dot.style.transform = `translateX(${-start * step}px) scale(${Math.min(scaleAt(fromStart), scaleAt(fromEnd))})`;
      dot.style.setProperty('--mimmu-dot-opacity', opacityAt(Math.abs(index - active)));
    });
  };

  const watch = (nav) => {
    if (watched.has(nav)) {
      return;
    }

    watched.add(nav);

    new MutationObserver(() => render(nav)).observe(nav, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-current', 'hidden'],
    });

    // la barra puo' apparire solo a certi breakpoint: si ridisegna quando cambia dimensione
    new ResizeObserver(() => render(nav)).observe(nav);

    // i pallini fuori finestra restano raggiungibili da tastiera: il fuoco non deve far scorrere la finestra
    nav.addEventListener('scroll', () => {
      nav.scrollLeft = 0;
    });

    render(nav);
  };

  const scan = () => root.querySelectorAll('carousel-navigation.page-dots').forEach(watch);

  scan();
  document.addEventListener('shopify:section:load', scan);

  // al cambio variante il tema rigenera la galleria: servono anche i pallini nuovi
  new MutationObserver(() => requestAnimationFrame(scan)).observe(root, { childList: true, subtree: true });
})();

/* STEFANO - Pagina prodotto: i video della galleria partono da soli (senza suono, in loop) quando sono visibili
   e si fermano quando escono dallo schermo, in qualsiasi layout della galleria. Il tema da solo li fa partire
   solo quando la foto viene selezionata nel carosello. Vale se la galleria ha l'autoplay attivo nelle impostazioni
   della sezione (attributo autoplay-media) e non con "riduci animazioni". Usa play() e pause() del tema, che
   gestiscono anche l'obbligo di suono spento e la pausa degli altri video. Solo video Shopify, non YouTube/Vimeo. */
(() => {
  const root = document.querySelector('.shopify-section--main-product');

  if (!root || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  const watched = new WeakSet();

  // il video si scarica un po' prima di servire
  const preloader = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const video = entry.target.querySelector('video');

          if (video && video.preload === 'none') {
            video.preload = 'metadata';
          }

          preloader.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '600px' }
  );

  const player = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.play();
        } else {
          entry.target.pause();
        }
      });
    },
    { threshold: 0.6 }
  );

  const scan = () => {
    root.querySelectorAll('product-gallery[autoplay-media] video-media[type="video"]').forEach((media) => {
      if (!watched.has(media)) {
        watched.add(media);
        preloader.observe(media);
        player.observe(media);
      }
    });
  };

  scan();
  document.addEventListener('shopify:section:load', scan);

  // al cambio variante il tema rigenera la galleria: servono anche i video nuovi
  new MutationObserver((records) => {
    if (records.some((record) => Array.from(record.addedNodes).some((node) => node.nodeType === 1 && (node.matches('video-media') || node.querySelector('video-media'))))) {
      requestAnimationFrame(scan);
    }
  }).observe(root, { childList: true, subtree: true });
})();

/* STEFANO - Ricerca: al tocco della lente il campo prende subito il focus. Su schermi touch il tema non lo fa (il focus
   automatico e' attivo solo con il mouse) e serve un secondo tocco sul campo. Il focus va dato nello stesso gesto del tocco,
   altrimenti il browser mobile non apre la tastiera. */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('a[aria-controls^="header-search"]');
  const input = trigger && document.querySelector(`#${trigger.getAttribute('aria-controls')} input[type="search"]`);

  if (input) {
    input.focus({ preventScroll: true });
  }
});

/* STEFANO - Ricerca: risultati esatti. La ricerca di Shopify trova anche dentro le parole (es. "erin" in "ballerine").
   - Menu a tendina: se almeno un prodotto ha tutte le parole cercate a inizio parola nel titolo, restano solo quelli;
     se non ce ne sono, si tolgono i prodotti esauriti (se resta almeno un disponibile). Con esatti si aggiungono, fino a
     10 prodotti, i disponibili della collezione di categoria del primo esatto (sezione mimmu-search-quick-related).
   - Pagina dei risultati (mimmu-main-search): sotto gli esatti i correlati sono i prodotti disponibili della collezione
     di categoria (calcolati nella sezione); solo se il prodotto non e' in nessuna, qui si caricano i correlati di Shopify
     (sezione mimmu-search-related, solo disponibili), senza quelli gia' mostrati. Se non ci sono esatti, non cambia nulla. */
(() => {
  const toWords = (text) => ` ${text.toLowerCase().replace(/[-/,.()]/g, ' ')}`;
  const isExact = (text, terms) => terms.every((term) => toWords(text).includes(` ${term}`));
  const productHandle = (card) => (card.querySelector('a[href*="/products/"]')?.getAttribute('href') || '').split('/products/')[1]?.split('?')[0];

  const QUICK_TOTAL = 10;
  const quickRelatedCache = new Map();

  const addQuickRelated = (list, firstExact) => {
    const handle = productHandle(firstExact);

    if (!handle || list.dataset.mimmuRelated === handle) {
      return;
    }

    list.dataset.mimmuRelated = handle;

    if (!quickRelatedCache.has(handle)) {
      quickRelatedCache.set(handle, fetch(`${window.Shopify.routes.root}products/${handle}?section_id=mimmu-search-quick-related`)
        .then((response) => (response.ok ? response.text() : ''))
        .catch(() => ''));
    }

    quickRelatedCache.get(handle).then((html) => {
      if (!html || !list.isConnected || list.dataset.mimmuRelated !== handle) {
        return;
      }

      const root = new DOMParser().parseFromString(html, 'text/html').querySelector('[id^="shopify-section"]');
      const title = root?.querySelector('.mimmu-quick-related-title');

      if (!root || !title) {
        return;
      }

      // dopo il titolo, per ogni prodotto ci sono due blocchi (desktop e mobile)
      const items = Array.from(root.children).filter((child) => child !== title);
      const shown = new Set(Array.from(list.children).map(productHandle));
      const room = QUICK_TOTAL - list.children.length / 2;
      const fresh = [];

      for (let index = 0; index + 1 < items.length; index += 2) {
        if (!shown.has(productHandle(items[index]))) {
          fresh.push(items[index], items[index + 1]);
        }
      }

      if (room > 0 && fresh.length > 0) {
        list.append(document.importNode(title, true), ...fresh.slice(0, room * 2).map((item) => document.importNode(item, true)));
      }
    });
  };

  const filterSuggestions = (results) => {
    const input = document.querySelector('header-search input[type="search"]');
    const terms = (input?.value || '').toLowerCase().split(/\s+/).filter(Boolean);
    const list = results.querySelector('.predictive-search__products');

    if (!list || terms.length === 0) {
      return;
    }

    // per ogni prodotto ci sono due blocchi (desktop e mobile), uno dopo l'altro
    const pairs = Array.from(list.children).reduce((all, child, index) => {
      if (index % 2 === 0) {
        all.push([child]);
      } else {
        all[all.length - 1].push(child);
      }

      return all;
    }, []);
    const exact = pairs.filter((pair) => isExact(pair[0].querySelector('.product-title')?.textContent || '', terms));

    if (exact.length > 0 && exact.length < pairs.length) {
      pairs.filter((pair) => !exact.includes(pair)).forEach((pair) => pair.forEach((child) => child.remove()));
    }

    // con risultati esatti, si aggiungono i correlati della stessa categoria (solo disponibili) fino a QUICK_TOTAL prodotti
    if (exact.length > 0) {
      addQuickRelated(list, exact[0][0]);
    }

    // senza esatti si lasciano solo i prodotti disponibili (il tema segna gli esauriti con il badge "sold-out", che usa
    // product.available: i prodotti con scorte non monitorate restano); se sono tutti esauriti non si tocca nulla
    if (exact.length === 0) {
      const soldOut = pairs.filter((pair) => pair.some((child) => child.querySelector('.badge--sold-out')));

      if (soldOut.length > 0 && soldOut.length < pairs.length) {
        soldOut.forEach((pair) => pair.forEach((child) => child.remove()));
      }
    }
  };

  const suggestions = document.querySelector('#header-predictive-search [slot="results"]');

  if (suggestions) {
    new MutationObserver(() => filterSuggestions(suggestions)).observe(suggestions, { childList: true });
  }

  const loadRelated = (box) => {
    box.dataset.loaded = 'true';

    fetch(`${window.Shopify.routes.root}recommendations/products?section_id=mimmu-search-related&product_id=${box.dataset.productId}&limit=12&intent=related`)
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        const list = new DOMParser().parseFromString(html, 'text/html').querySelector('product-list');
        const shown = new Set(Array.from(box.parentElement.querySelectorAll(':scope > product-list product-card')).map(productHandle));

        if (!list) {
          return;
        }

        Array.from(list.children).forEach((card) => {
          if (shown.has(productHandle(card))) {
            card.remove();
          }
        });

        while (list.children.length > 8) {
          list.lastElementChild.remove();
        }

        if (list.children.length > 0) {
          box.querySelector('[data-content]').replaceChildren(document.importNode(list, true));
          box.hidden = false;
        }
      })
      .catch(() => {});
  };

  if (document.querySelector('.main-search')) {
    const scan = () => document.querySelectorAll('.mimmu-search-related:not([data-loaded])').forEach(loadRelated);

    scan();
    // dopo un filtro il tema rigenera i risultati
    new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
  }
})();

/* STEFANO - Pagina fidelity-program (Loloyal): vantaggi dei livelli VIP. L'app disegna le card dei livelli ma non ha un
   campo per i vantaggi, quindi si scrivono qui (id del livello nell'app -> testi per lingua) dentro la parte vuota
   della card. Parte solo se nella pagina c'e' il blocco dei livelli (#loyalty-vip-tier). Stile in custom.css. */
(() => {
  const root = document.getElementById('loyalty-vip-tier');

  if (!root) {
    return;
  }

  const benefits = {
    // Member
    "81350": {
      it: [
        "Sconto del 15% al compleanno",
      ],
      en: [
        "15% birthday discount",
      ],
      fr: [
        "Réduction de 15% pour votre anniversaire",
      ],
      de: [
        "15% Rabatt an Ihrem Geburtstag",
      ],
      es: [
        "Descuento del 15% en tu cumpleaños",
      ],
      pt: [
        "Desconto de 15% no aniversário",
      ],
    },
    // Bronze
    "67711": {
      it: [
        "Fino al 15% di sconto utilizzando i tuoi punti",
        "Sconto del 15% al compleanno",
      ],
      en: [
        "Up to 15% discount using your points",
        "15% birthday discount",
      ],
      fr: [
        "Jusqu'à 15% de réduction en utilisant vos points",
        "Réduction de 15% pour votre anniversaire",
      ],
      de: [
        "Bis zu 15% Rabatt mit Ihren Punkten",
        "15% Rabatt an Ihrem Geburtstag",
      ],
      es: [
        "Hasta un 15% de descuento usando tus puntos",
        "Descuento del 15% en tu cumpleaños",
      ],
      pt: [
        "Até 15% de desconto usando os seus pontos",
        "Desconto de 15% no aniversário",
      ],
    },
    // Silver
    "67712": {
      it: [
        "Fino al 20% di sconto utilizzando i tuoi punti",
        "Sconto del 15% al compleanno",
      ],
      en: [
        "Up to 20% discount using your points",
        "15% birthday discount",
      ],
      fr: [
        "Jusqu'à 20% de réduction en utilisant vos points",
        "Réduction de 15% pour votre anniversaire",
      ],
      de: [
        "Bis zu 20% Rabatt mit Ihren Punkten",
        "15% Rabatt an Ihrem Geburtstag",
      ],
      es: [
        "Hasta un 20% de descuento usando tus puntos",
        "Descuento del 15% en tu cumpleaños",
      ],
      pt: [
        "Até 20% de desconto usando os seus pontos",
        "Desconto de 15% no aniversário",
      ],
    },
    // Gold
    "67713": {
      it: [
        "Fino al 25% di sconto utilizzando i tuoi punti",
        "Sconto del 15% al compleanno",
        "Accesso Anticipato ai Lanci ed alle Promo",
        "Sconti Esclusivi su WhatsApp e via Email",
        "Spedizione Sempre Gratuita",
      ],
      en: [
        "Up to 25% discount using your points",
        "15% birthday discount",
        "Early Access to Launches and Promos",
        "Exclusive Discounts via WhatsApp and Email",
        "Always Free Shipping",
      ],
      fr: [
        "Jusqu'à 25% de réduction en utilisant vos points",
        "Réduction de 15% pour votre anniversaire",
        "Accès Anticipé aux Lancements et Promos",
        "Réductions Exclusives via WhatsApp et Email",
        "Livraison Toujours Gratuite",
      ],
      de: [
        "Bis zu 25% Rabatt mit Ihren Punkten",
        "15% Rabatt an Ihrem Geburtstag",
        "Frühzeitiger Zugang zu Launches und Aktionen",
        "Exklusive Rabatte per WhatsApp und E-Mail",
        "Immer Kostenloser Versand",
      ],
      es: [
        "Hasta un 25% de descuento usando tus puntos",
        "Descuento del 15% en tu cumpleaños",
        "Acceso Anticipado a Lanzamientos y Promos",
        "Descuentos Exclusivos por WhatsApp y Email",
        "Envío Siempre Gratuito",
      ],
      pt: [
        "Até 25% de desconto usando os seus pontos",
        "Desconto de 15% no aniversário",
        "Acesso Antecipado a Lançamentos e Promos",
        "Descontos Exclusivos via WhatsApp e Email",
        "Envio Sempre Gratuito",
      ],
    },
    // Elite
    "82502": {
      it: [
        "Fino al 30% di sconto utilizzando i tuoi punti",
        "Sconto del 15% al compleanno",
        "Accesso Anticipato ai Lanci ed alle Promo",
        "Sconti Esclusivi su WhatsApp e via Email",
        "Spedizione Sempre Gratuita",
        "Possibilità di creare la scarpa dei tuoi sogni (che riceverai in Regalo)",
      ],
      en: [
        "Up to 30% discount using your points",
        "15% birthday discount",
        "Early Access to Launches and Promos",
        "Exclusive Discounts via WhatsApp and Email",
        "Always Free Shipping",
        "Possibility to create your dream shoe (which you will receive as a Gift)",
      ],
      fr: [
        "Jusqu'à 30% de réduction en utilisant vos points",
        "Réduction de 15% pour votre anniversaire",
        "Accès Anticipé aux Lancements et Promos",
        "Réductions Exclusives via WhatsApp et Email",
        "Livraison Toujours Gratuite",
        "Possibilité de créer la chaussure de vos rêves (que vous recevrez en Cadeau)",
      ],
      de: [
        "Bis zu 30% Rabatt mit Ihren Punkten",
        "15% Rabatt an Ihrem Geburtstag",
        "Frühzeitiger Zugang zu Launches und Aktionen",
        "Exklusive Rabatte per WhatsApp und E-Mail",
        "Immer Kostenloser Versand",
        "Möglichkeit, Ihren Traumschuh zu kreieren (den Sie als Geschenk erhalten)",
      ],
      es: [
        "Hasta un 30% de descuento usando tus puntos",
        "Descuento del 15% en tu cumpleaños",
        "Acceso Anticipado a Lanzamientos y Promos",
        "Descuentos Exclusivos por WhatsApp y Email",
        "Envío Siempre Gratuito",
        "Posibilidad de crear el zapato de tus sueños (que recibirás de Regalo)",
      ],
      pt: [
        "Até 30% de desconto usando os seus pontos",
        "Desconto de 15% no aniversário",
        "Acesso Antecipado a Lançamentos e Promos",
        "Descontos Exclusivos via WhatsApp e Email",
        "Envio Sempre Gratuito",
        "Possibilidade de criar o sapato dos seus sonhos (que receberá de Presente)",
      ],
    },
  };

  const language = (document.documentElement.lang || 'it').slice(0, 2);

  // le card le crea l'app dopo il caricamento (e le ridisegna in certi casi): si compila a ogni cambio, una volta per card
  const fill = () => {
    root.querySelectorAll('.loyalty-vip-tier-card[data-tier-id]').forEach((card) => {
      const texts = benefits[card.dataset.tierId];
      const inner = card.querySelector('.loyalty-vip-tier-benefits-inner');

      if (!texts || !inner || inner.querySelector('.mimmu-tier-benefits')) {
        return;
      }

      const list = document.createElement('ul');

      list.className = 'mimmu-tier-benefits';
      (texts[language] || texts.it).forEach((text) => {
        const item = document.createElement('li');

        item.textContent = text;
        list.append(item);
      });
      inner.append(list);
    });
  };

  fill();
  new MutationObserver(fill).observe(root, { childList: true, subtree: true });
})();

/* STEFANO - Apertura del carrello Upcart dopo l'aggiunta al carrello.
   Upcart apre il cassetto solo se il suo intercettore di window.fetch vede la chiamata a /cart/add. L'app Omega
   (data di consegna stimata) a volte ripristina un window.fetch salvato prima che Upcart si installasse, e
   l'intercettore sparisce: il prodotto si aggiunge ma il cassetto resta chiuso (succede a caso, al caricamento).
   L'evento "added-to-cart" di Upcart invece arriva sempre: se a quel punto il cassetto non si e' aperto da solo,
   si aggiorna il carrello di Upcart (altrimenti mostrerebbe il contenuto vecchio) e lo si apre. */
(() => {
  const events = 'aftersell-upcart:public-events:';
  let open = false;

  window.addEventListener(`${events}cart-opened`, () => {
    open = true;
  });

  window.addEventListener(`${events}cart-closed`, () => {
    open = false;
  });

  window.addEventListener(`${events}added-to-cart`, async () => {
    if (open || typeof window.upcartOpenCart !== 'function') {
      return;
    }

    try {
      await window.upcartRefreshCart?.();
    } catch (error) {
      console.error(error);
    }

    window.upcartOpenCart();
  });
})();

/* STEFANO - Footer, modulo di Shopify Forms (sconto di benvenuto): l'app lo disegna dentro un contenitore isolato
   (shadow DOM), che il CSS del tema non raggiunge. Qui si aggiunge al contenitore un foglio di stile con titolo piu'
   sottile e piccolo (come i titoli delle colonne del footer), campi e pulsante squadrati come il resto del tema e
   spazi ridotti. I nomi delle classi dell'app finiscono con un codice che cambia (es. _textHeading_2aowh_35): si
   usa solo la parte stabile. Vale solo per il modulo dentro il footer, non per i popup. Se l'app cambia i nomi, il
   modulo torna al suo aspetto normale senza rompersi. */
(() => {
  const css = `
    section[class*="_formHeader_"] { margin: 0 auto 4px; }
    h2[class*="_textHeading_"] { margin: 0 0 8px; font-size: 14px; font-weight: 600; letter-spacing: 0.1em; line-height: 1.4; text-transform: uppercase; }
    [class*="_textBody_"] { font-size: 13px; line-height: 1.5; }
    form[class*="_formFieldset_"] { gap: 8px; }
    [class*="_formInputField_"] { border-radius: 0; }
    button[class*="_formSubmitButton_"] { border-radius: 0; font-size: 12px; letter-spacing: 0.18em; text-transform: uppercase; }
    div[class*="_formDisclaimer_"] { margin-top: 8px; }
  `;

  let sheet = null;

  try {
    sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
  } catch (error) {
    sheet = null;
  }

  const apply = (host) => {
    const root = host.shadowRoot;

    if (!root || host.dataset.customStyled || !host.closest('.footer__block--app')) {
      return;
    }

    host.dataset.customStyled = 'true';

    if (sheet) {
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
    } else {
      const style = document.createElement('style');

      style.textContent = css;
      root.appendChild(style);
    }
  };

  const scan = () => document.querySelectorAll('.footer__block--app shopify-forms-embed').forEach(apply);

  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
  scan();
})();

/* STEFANO - Trustpilot: lo script dei widget parte da solo solo a fine caricamento della pagina (evento load, anche dopo
   5-10 secondi per le altre app). Qui lo si carica appena la pagina e' pronta, se ci sono widget. */
(function () {
  const url = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';

  const start = () => {
    if (!document.querySelector('.trustpilot-widget') || document.querySelector(`script[src="${url}"]`)) {
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = url;
    document.head.appendChild(script);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* STEFANO - Pagina prodotto, galleria su mobile: all'ultima foto uno swipe in avanti riporta alla prima, e alla prima uno
   swipe indietro porta all'ultima (il carosello nativo si ferma ai bordi). Non fa nulla se la galleria non scorre. */
(function () {
  const threshold = 20;
  const quickThreshold = 12;
  const quickTime = 250;
  let start = null;

  document.addEventListener(
    'touchstart',
    (event) => {
      const carousel = event.target.closest && event.target.closest('.product-gallery__carousel');
      const touch = event.touches[0];

      start = null;

      if (!carousel || event.touches.length !== 1 || carousel.scrollWidth <= carousel.clientWidth + 1) {
        return;
      }

      if (getComputedStyle(carousel).direction === 'rtl') {
        return;
      }

      const max = carousel.scrollWidth - carousel.clientWidth;

      start = {
        carousel,
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
        atStart: carousel.scrollLeft <= carousel.clientWidth * 0.1,
        atEnd: carousel.scrollLeft >= max - carousel.clientWidth * 0.1,
        max
      };
    },
    { passive: true }
  );

  document.addEventListener(
    'touchend',
    (event) => {
      if (!start) {
        return;
      }

      const { carousel, atStart, atEnd, max } = start;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      const elapsed = Date.now() - start.time;

      start = null;

      const minimum = elapsed < quickTime ? quickThreshold : threshold;

      if (Math.abs(dx) < minimum || Math.abs(dx) <= Math.abs(dy)) {
        return;
      }

      if (dx < 0 && atEnd) {
        carousel.scrollTo({ left: 0, behavior: 'smooth' });
      } else if (dx > 0 && atStart) {
        carousel.scrollTo({ left: max, behavior: 'smooth' });
      }
    },
    { passive: true }
  );
})();
